import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { X as XIcon, ListDashes, Image as ImageIcon, VideoCamera, TextT } from "@phosphor-icons/react";
import ChatMessage from "./chat/ChatMessage";
import ChatComposer from "./chat/ChatComposer";

const FILTERS = [
  { id: "all", label: "Tous", icon: ListDashes },
  { id: "photos", label: "Photos", icon: ImageIcon },
  { id: "videos", label: "Vidéos", icon: VideoCamera },
  { id: "titles", label: "Titres", icon: TextT },
];

export default function ChatFeed({ chantierId = null, placeholder = "Écrire un message…", onClose = null, title: pageTitle = null }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [postTitle, setPostTitle] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pickerFor, setPickerFor] = useState(null);
  const [filter, setFilter] = useState("all");
  const scrollRef = useRef(null);

  // News feed enables the "Title" field + filters ; chantier chat stays lean.
  const isNewsFeed = !chantierId;

  const load = async () => {
    try {
      const params = chantierId ? { chantier_id: chantierId } : {};
      const r = await api.get("/chat", { params });
      setMessages(r.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [chantierId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  // close picker on outside click / escape
  useEffect(() => {
    if (!pickerFor) return;
    const onKey = (e) => { if (e.key === "Escape") setPickerFor(null); };
    const onClick = (e) => {
      if (!e.target.closest?.("[data-reaction-picker]") && !e.target.closest?.("[data-reaction-trigger]")) {
        setPickerFor(null);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("click", onClick); };
  }, [pickerFor]);

  const send = async () => {
    const t = text.trim();
    const ttl = postTitle.trim();
    if (!t && files.length === 0 && !ttl) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (chantierId) fd.append("chantier_id", chantierId);
      fd.append("text", t);
      if (ttl) fd.append("title", ttl);
      // Order matters: the backend consumes `posters` in the same order as
      // videos appear in `files`, so we walk both lists together.
      files.forEach((f) => {
        fd.append("files", f);
        if ((f.type || "").startsWith("video/") && f._poster) {
          fd.append("posters", f._poster);
        }
      });
      await api.post("/chat", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText("");
      setPostTitle("");
      setFiles([]);
      load();
      toast.success(isNewsFeed ? "Publié sur le fil" : "Message envoyé");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const sendGif = async (url) => {
    if (!url) return;
    try {
      const fd = new FormData();
      if (chantierId) fd.append("chantier_id", chantierId);
      fd.append("gif_url", url);
      fd.append("text", "");
      await api.post("/chat", fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await api.delete(`/chat/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canDelete = (m) => m.user_id === user?.id || ["admin", "associate"].includes(user?.role);

  // Karim requested a 5-min edit window — same rule enforced backend-side.
  const canEdit = (m) => {
    if (m.user_id !== user?.id) return false;
    if (!m.created_at) return false;
    const created = new Date(m.created_at).getTime();
    if (!Number.isFinite(created)) return false;
    return Date.now() - created < 5 * 60 * 1000;
  };

  const editMessage = async (id, patch) => {
    try {
      const r = await api.patch(`/chat/${id}`, patch);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...r.data } : m)));
      toast.success("Message modifié");
    } catch (err) {
      toast.error(formatApiError(err));
      throw err;
    }
  };

  // Filter messages according to the selected news-feed tab.
  const visibleMessages = useMemo(() => {
    if (!isNewsFeed || filter === "all") return messages;
    return messages.filter((m) => {
      const atts = m.attachments || [];
      if (filter === "photos") return atts.some((a) => a.kind === "photo" || a.kind === "gif");
      if (filter === "videos") return atts.some((a) => a.kind === "video");
      if (filter === "titles") return !!(m.title && m.title.trim());
      return true;
    });
  }, [messages, filter, isNewsFeed]);

  const react = async (msgId, emoji) => {
    // Optimistic update
    setMessages((prev) => prev.map((m) => {
      if (m.id !== msgId) return m;
      const reactions = { ...(m.reactions || {}) };
      const arr = [...(reactions[emoji] || [])];
      const idx = arr.indexOf(user?.id);
      if (idx >= 0) arr.splice(idx, 1); else arr.push(user?.id);
      if (arr.length) reactions[emoji] = arr; else delete reactions[emoji];
      return { ...m, reactions };
    }));
    setPickerFor(null);
    try {
      const fd = new FormData();
      fd.append("emoji", emoji);
      const r = await api.post(`/chat/${msgId}/react`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, reactions: r.data.reactions } : m));
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[400px] bg-black/40 rounded-2xl border border-white/10 overflow-hidden" data-testid="chat-feed">
      {(onClose || pageTitle) && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 bg-white/[0.02] shrink-0">
          {onClose && (
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 active:scale-95 transition-all"
              title="Fermer"
              data-testid="chat-close-btn"
            >
              <XIcon size={14} weight="bold" />
            </button>
          )}
          <div className="text-xs font-semibold text-white/80 truncate">
            {pageTitle || (chantierId ? "Chat chantier" : "Fil d'actualité")}
          </div>
        </div>
      )}
      {isNewsFeed && (
        <div className="flex items-center gap-1 px-3 py-2 border-b border-white/10 bg-black/30 shrink-0 overflow-x-auto" data-testid="feed-filters">
          {FILTERS.map(({ id, label, icon: Icon }) => {
            const active = filter === id;
            const count = id === "all" ? messages.length : messages.filter((m) => {
              const atts = m.attachments || [];
              if (id === "photos") return atts.some((a) => a.kind === "photo" || a.kind === "gif");
              if (id === "videos") return atts.some((a) => a.kind === "video");
              if (id === "titles") return !!(m.title && m.title.trim());
              return true;
            }).length;
            return (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full transition shrink-0 ${active ? "bg-[#005CFF] text-white" : "bg-white/5 hover:bg-white/10 text-white/60"}`}
                data-testid={`feed-filter-${id}`}
              >
                <Icon size={12} weight="bold" />
                {label}
                <span className={`text-[9px] font-bold tabular-nums px-1 rounded ${active ? "bg-black/25" : "bg-white/10 text-white/50"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
        {loading && <div className="text-center text-xs text-white/40 py-4">Chargement…</div>}
        {!loading && visibleMessages.length === 0 && (
          <div className="text-center text-xs text-white/40 py-10">
            {messages.length === 0
              ? (chantierId ? "Aucun message. Soyez le premier à poster sur ce chantier." : "Aucun message. Ouvrez le fil d'actualité de BKPRO.")
              : "Aucun message ne correspond à ce filtre."}
          </div>
        )}
        {visibleMessages.map((m) => (
          <ChatMessage
            key={m.id}
            message={m}
            currentUser={user}
            pickerFor={pickerFor}
            onOpenPicker={setPickerFor}
            onReact={react}
            onDelete={remove}
            onEdit={editMessage}
            canDelete={canDelete(m)}
            canEdit={canEdit(m)}
          />
        ))}
      </div>

      <ChatComposer
        text={text}
        setText={setText}
        title={postTitle}
        setTitle={setPostTitle}
        files={files}
        setFiles={setFiles}
        sending={sending}
        placeholder={placeholder}
        onSend={send}
        onSendGif={sendGif}
        showTitle={isNewsFeed}
      />
    </div>
  );
}
